import { useCallback, useContext, useState, type FormEvent } from "react";

import { ProjectContext } from "../app/projectContext";
import {
  cableSizingRequestSchema,
  type CableSizingRequest,
} from "../services/cable";
import type { FaultRunFaultLevel } from "../services/faultLink";
import type { SldElements } from "../services/sldElements";
import { useStudyDraft } from "../hooks/useStudyDraft";
import { formatQuantity } from "../utils/formatQuantity";
import { describeValidationIssue } from "../utils/validationMessages";
import { CABLE_SIZING_LABELS } from "./cableSizingLabels";
import {
  buildCableSizingPayload,
  cableSizingDraftSchema,
  createInitialCableSizingDraft,
  faultCurrentSourceSchema,
  FAULT_CURRENT_SOURCE_LABELS,
  type CableSizingDraft,
  type FaultCurrentSource,
} from "./cableSizingDraft";
import { FaultRunSelector } from "./FaultRunSelector";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker, sldElementsOf } from "./SldElementPicker";

// A cable study's short-circuit current may be read from a stored EOS-04 fault run of the
// bus its feeder leaves (SLD-b3, A28 (e)). The run gives the current only: the fault
// duration stays the engineer's, and a run that is not the maximum three-phase fault of
// that bus is recorded in the stored run's fault link for review, not refused.
export const LINKED_DURATION_HINT =
  "The linked fault run gives the short-circuit current; the fault duration stays yours.";

export const NOT_MAXIMUM_FAULT_RUN_NOTE =
  "This fault run is not a maximum three-phase study; the stored run's fault current source " +
  "will be Engineering review required.";

/** "Fault current from SC-MSB-01 rev 1 (bus MSB-01): 35.22 kA" */
export function linkedFaultCurrentText(level: FaultRunFaultLevel): string {
  const current = formatQuantity(level.initial_symmetrical_short_circuit_current_ka, "kA");
  return (
    `Fault current from ${level.calculation_key} rev ${level.revision_number} ` +
    `(bus ${level.fault_bus_code}): ${current.display}`
  );
}

type CableSizingFormProps = {
  disabled?: boolean;
  onSubmit: (payload: CableSizingRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): a feeder is
  // picked from it, and its cable length and bus voltage prefill the blank fields (A28 (a)).
  sldElements?: SldElements | null | undefined;
};

export function CableSizingForm({ disabled = false, onSubmit, sldElements }: CableSizingFormProps) {
  // Tolerant of a missing provider (no project, no revision), like the fault form.
  const revisionId = useContext(ProjectContext)?.activeRevisionId() ?? null;
  // The entries outlive a reload, Back and the sidebar for as long as the tab is open.
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<CableSizingDraft>({
      module: "cable-sizing",
      createInitial: createInitialCableSizingDraft,
      schema: cableSizingDraftSchema,
      revisionId,
    });
  const [validationError, setValidationError] = useState<string | null>(null);
  // The fault run the shared selector read, for the figure shown beside it (A12).
  const [linkedLevel, setLinkedLevel] = useState<FaultRunFaultLevel | null>(null);

  function updateField<K extends keyof CableSizingDraft>(
    field: K,
    value: CableSizingDraft[K],
  ) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  // The feeder picked from the diagram: the route length from the length of its cable and the
  // nominal voltage from its bus, each only while the field is blank, never over a value the
  // engineer typed (A28 (a)). The cable of a feeder is the cable element whose branch is it.
  function chooseFeeder(code: string) {
    const elements = sldElements?.elements ?? [];
    const feeder = elements.find((item) => item.category === "feeder" && item.code === code);
    const cable = elements.find(
      (item) => item.category === "cable" && item.upstream_paths?.[0]?.[0] === code,
    );
    const bus = elements.find((item) => item.category === "bus" && item.code === feeder?.bus);
    setDraft((current) => ({
      ...current,
      sldElementCode: code,
      routeLengthM:
        current.routeLengthM.trim() === "" && cable?.rating ? cable.rating : current.routeLengthM,
      nominalVoltageV:
        current.nominalVoltageV.trim() === "" && bus?.voltage_v
          ? bus.voltage_v
          : current.nominalVoltageV,
    }));
  }

  // The chosen fault run, and the level the selector read with it: the selector drops a run
  // the revision no longer holds, so both callbacks stay stable across renders.
  const setFaultRunId = useCallback(
    (faultRunId: string) => setDraft((current) => ({ ...current, faultRunId })),
    [setDraft],
  );

  const onLinked = useCallback((level: FaultRunFaultLevel | null) => setLinkedLevel(level), []);

  // One source of the short-circuit current at a time (A28 (e)): the figure the engineer
  // typed is cleared when the link is chosen, and the chosen run when the figure is.
  function chooseFaultCurrentSource(source: FaultCurrentSource) {
    setDraft((current) => ({
      ...current,
      faultCurrentSource: source,
      faultCurrentKa: source === "LINKED_FAULT_RUN" ? "" : current.faultCurrentKa,
      faultRunId: source === "TYPED" ? "" : current.faultRunId,
    }));
  }

  // Empties the form only; a result already on the page stays until "Clear results".
  function clearForm() {
    setValidationError(null);
    clear();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = cableSizingRequestSchema.safeParse(buildCableSizingPayload(draft));

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(
        issue
          ? describeValidationIssue(issue, CABLE_SIZING_LABELS)
          : "Review the cable sizing inputs.",
      );
      return;
    }

    await onSubmit(parsed.data);
  }

  // The bus the chosen feeder leaves, as the diagram holds it: the selector says when the
  // fault run is a fault at another bus, and the backend checks the saved study regardless.
  const feederBus =
    (sldElements?.elements ?? []).find((item) => item.code === draft.sldElementCode)?.bus ?? "";

  const text = (
    label: string,
    field: keyof CableSizingDraft,
    decimal = false,
  ) => (
    <label>
      {label}
      <input
        inputMode={decimal ? "decimal" : undefined}
        name={field}
        value={String(draft[field])}
        onChange={(event) => updateField(field, event.target.value as never)}
      />
    </label>
  );

  const check = (label: string, field: keyof CableSizingDraft) => (
    <label>
      <input
        type="checkbox"
        name={field}
        checked={Boolean(draft[field])}
        onChange={(event) => updateField(field, event.target.checked as never)}
      />
      {label}
    </label>
  );

  return (
    <form aria-label="Cable sizing inputs" onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice
          onKeep={keepForThisRevision}
          onClear={clearForm}
          disabled={disabled}
        />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>
        {text("Study code", "studyCode")}
        {text("Study name", "studyName")}
        {text("Notes", "notes")}
        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateField("jurisdictionProfile", event.target.value)}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">Australia / New Zealand (AS/NZS 3000) - reference data pending</option>
          </select>
        </label>
        <p>
          The jurisdiction profile governs reference precedence and ambient
          conventions; engine physics does not change with the profile.
        </p>
        <p>
          References: IEC 60364-5-52 (selection) and IEC 60287 (ampacity) as
          registered in the reference register; editions are unverified until
          confirmed there.
        </p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Circuit</legend>
        {sldElementsOf(sldElements, ["feeder"]).length > 0 ? (
          <SldElementPicker
            label="Feeder from the diagram"
            name="sldElementCode"
            value={draft.sldElementCode}
            onChange={chooseFeeder}
            categories={["feeder"]}
            elements={sldElements}
          />
        ) : null}
        {text("Design current (A)", "designCurrentA", true)}
        {text("Nominal voltage (V)", "nominalVoltageV", true)}
        {text("Route length (m)", "routeLengthM", true)}
        <label>
          System
          <select
            name="system"
            value={draft.system}
            onChange={(event) => updateField("system", event.target.value)}
          >
            <option value="">Select system</option>
            <option value="DC_TWO_WIRE">DC two-wire</option>
            <option value="SINGLE_PHASE_AC">Single-phase AC</option>
            <option value="THREE_PHASE_THREE_WIRE">Three-phase three-wire</option>
            <option value="THREE_PHASE_FOUR_WIRE">Three-phase four-wire</option>
          </select>
        </label>
        {text("Power factor", "powerFactor", true)}
        {text("Allowable voltage drop (%)", "allowableVoltageDropPercent", true)}
        <label>
          Short-circuit current source
          <select
            name="faultCurrentSource"
            value={draft.faultCurrentSource}
            onChange={(event) =>
              chooseFaultCurrentSource(event.target.value as FaultCurrentSource)
            }
          >
            {faultCurrentSourceSchema.options.map((source) => (
              <option key={source} value={source}>
                {FAULT_CURRENT_SOURCE_LABELS[source]}
              </option>
            ))}
          </select>
        </label>
        {draft.faultCurrentSource === "LINKED_FAULT_RUN" ? (
          <>
            <FaultRunSelector
              revisionId={revisionId}
              faultRunId={draft.faultRunId}
              onChange={setFaultRunId}
              busCode={feederBus}
              withPeak={false}
              notMaximumNote={NOT_MAXIMUM_FAULT_RUN_NOTE}
              onLinked={onLinked}
            />
            {linkedLevel !== null ? (
              <p
                data-linked-fault-current={linkedLevel.run_id}
                title={
                  formatQuantity(
                    linkedLevel.initial_symmetrical_short_circuit_current_ka,
                    "kA",
                  ).exact ?? ""
                }
              >
                {linkedFaultCurrentText(linkedLevel)}
              </p>
            ) : null}
            <p data-linked-duration-hint="true">{LINKED_DURATION_HINT}</p>
          </>
        ) : (
          text("Fault current (kA)", "faultCurrentKa", true)
        )}
        {text("Fault duration (s)", "faultDurationS", true)}
        {text("Harmonic neutral factor", "harmonicNeutralFactor", true)}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Cable</legend>
        <label>
          Conductor material
          <select
            name="conductorMaterial"
            value={draft.conductorMaterial}
            onChange={(event) => updateField("conductorMaterial", event.target.value)}
          >
            <option value="">Select material</option>
            <option value="COPPER">Copper</option>
            <option value="ALUMINIUM">Aluminium</option>
          </select>
        </label>
        <label>
          Insulation
          <select
            name="insulationMaterial"
            value={draft.insulationMaterial}
            onChange={(event) => updateField("insulationMaterial", event.target.value)}
          >
            <option value="">Select insulation</option>
            <option value="PVC">PVC</option>
            <option value="XLPE">XLPE</option>
            <option value="EPR">EPR</option>
            <option value="MINERAL">Mineral</option>
          </select>
        </label>
        <label>
          Construction
          <select
            name="construction"
            value={draft.construction}
            onChange={(event) => updateField("construction", event.target.value)}
          >
            <option value="">Select construction</option>
            <option value="SINGLE_CORE">Single-core</option>
            <option value="MULTICORE">Multicore</option>
          </select>
        </label>
        <label>
          Arrangement
          <select
            name="arrangement"
            value={draft.arrangement}
            onChange={(event) => updateField("arrangement", event.target.value)}
          >
            <option value="">Select arrangement</option>
            <option value="MULTICORE">Multicore</option>
            <option value="FLAT_TOUCHING">Flat touching</option>
            <option value="FLAT_SPACED">Flat spaced</option>
            <option value="TREFOIL_TOUCHING">Trefoil touching</option>
            <option value="TREFOIL_SPACED">Trefoil spaced</option>
          </select>
        </label>
        {text("Loaded conductors", "numberOfLoadedConductors")}
        {text("Parallel runs", "parallelRuns")}
        <label>
          Protective conductor
          <select
            name="protectiveConductorType"
            value={draft.protectiveConductorType}
            onChange={(event) => updateField("protectiveConductorType", event.target.value)}
          >
            <option value="">Backend default</option>
            <option value="INTEGRAL_CORE">Integral core</option>
            <option value="SEPARATE_INSULATED">Separate insulated</option>
            <option value="SEPARATE_BARE">Separate bare</option>
            <option value="METALLIC_SCREEN">Metallic screen</option>
            <option value="METALLIC_ARMOUR">Metallic armour</option>
            <option value="NONE">None</option>
          </select>
        </label>
        {check("Neutral required", "neutralRequired")}
        {check("Reduced neutral permitted", "reducedNeutralPermitted")}
        {check("Armoured", "armoured")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Installation</legend>
        <label>
          Installation method
          <select
            name="method"
            value={draft.method}
            onChange={(event) => updateField("method", event.target.value)}
          >
            <option value="">Select method</option>
            <option value="A1_INSULATED_WALL_CONDUIT">A1 insulated wall, conduit</option>
            <option value="A2_INSULATED_WALL_MULTICORE">A2 insulated wall, multicore</option>
            <option value="B1_WALL_CONDUIT_SINGLE_CORE">B1 wall conduit, single-core</option>
            <option value="B2_WALL_CONDUIT_MULTICORE">B2 wall conduit, multicore</option>
            <option value="C_CLIPPED_DIRECT">C clipped direct</option>
            <option value="D1_GROUND_DUCT">D1 ground duct</option>
            <option value="D2_DIRECT_BURIED">D2 direct buried</option>
            <option value="E_FREE_AIR_MULTICORE">E free air, multicore</option>
            <option value="F_FREE_AIR_TOUCHING_SINGLE_CORE">F free air, touching single-core</option>
            <option value="G_FREE_AIR_SPACED_SINGLE_CORE">G free air, spaced single-core</option>
            <option value="CABLE_TRAY">Cable tray</option>
            <option value="CABLE_LADDER">Cable ladder</option>
            <option value="ENGINEERED_IEC_60287">Engineered (IEC 60287)</option>
          </select>
        </label>
        {text("Ambient temperature (°C)", "ambientTemperatureC", true)}
        {text("Ambient derating factor", "ambientDeratingFactor", true)}
        {text("Grouping derating factor", "groupingDeratingFactor", true)}
        {text("Thermal insulation factor", "thermalInsulationFactor", true)}
        {text("Depth derating factor", "depthDeratingFactor", true)}
        {text("Soil thermal resistivity factor", "soilThermalResistivityFactor", true)}
        {text("Grouped circuits", "groupedCircuits")}
        {text("Burial depth (m)", "burialDepthM", true)}
        {text("Soil thermal resistivity (K·m/W)", "soilThermalResistivityKMPerW", true)}
        {text("Conductor spacing (mm)", "conductorSpacingMm", true)}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Size schedule (mm², comma-separated, ascending)</legend>
        {text("Phase sizes", "phaseSizesMm2")}
        {text("Neutral sizes", "neutralSizesMm2")}
        {text("Protective sizes", "protectiveSizesMm2")}
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Calculate cable sizing
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}
